// Material draußen (seit E3b, O65 – Manuels großer Wunsch, Gernots
// Entscheidung vom 22.10.2026): Der Verkäufer schreibt schon draußen auf, was
// er an Material sieht. Zwei Wege, eine Seite:
//
//   #/aufnahme/<kennung>/material              am Schluss, nach Station 7 –
//                                              alles Material der Besichtigung,
//                                              je Zeile, aus welcher Station
//   #/aufnahme/<kennung>/station/<nr>/material über den Knopf „Material“ in
//                                              einer Station – nur ihres
//
// Oben steht, was schon eingetragen ist – die Menge zum Ändern, ✕ zum
// Entfernen. Darunter „Zum Antippen“: die Standardliste des Büros als
// Kacheln (Varianten wie drei Kabelkanäle sind EINE Kachel, darunter die
// Größen) und immer „Etwas anderes“ zum freien Tippen. Antippen öffnet an
// derselben Stelle das Formular „Dazu“ – eins nach dem anderen, wie auf dem
// Rechner. Die Liste liegt am Gerät, „Material“ geht auch im Keller ohne
// Netz (material.ts).
//
// Freiwillig: kein „fertig“, Senden geht auch ohne Material – das Büro
// entscheidet bei der Freigabe ohnehin (Grundsatz 7).
//
// Gesendet: nur zum Lesen, mit den Marken „nachgereicht“ und „berichtigt“.
// Im Nachtrag: Neues kommt dazu; bei Material, das schon im Büro ist, lässt
// sich die Menge berichtigen, entfernen nicht (0 eintragen) – die Regeln der
// Längen (arbeitskopie.ts).

import { useEffect, useRef, useState } from "react";
import { STATIONEN, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, Chip, Ikon, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { materialAendern, materialDazu, materialEntfernen, materialMenge, type Bearbeiten } from "../arbeitskopie.ts";
import {
  entfernbar,
  formularPruefen,
  textAenderbar,
  kacheln,
  materialMarke,
  materialOrdnen,
  materialText,
  mengeLesen,
  mengeZeigen,
  standardlisteAnstossen,
  standardlisteLesen,
  variantenName,
  vorgabeText,
  type Kachel,
} from "../material.ts";
import { STATIONEN_ZAHL } from "../postausgang.ts";
import type { Materialangabe, StandardEintrag, Station } from "../speicher.ts";
import { aufnahmeLesen, modusVon, type AufnahmeGanz } from "./Uebersicht.tsx";
import { SendeStand, speichernFehlgeschlagen, useLive, usePostausgang, useSpeichern, ZUM_START } from "./gemeinsam.tsx";

/**
 * Woher ein Material kommt, für Menschen – „aus Station 2: Weg vom Dach zum
 * Technikraum“, wie oben auf der Seite einer Station. Mit dem Wort „Station“
 * und der Nummer, weil ein langer Name allein verwirrt (Gernot, Augentest
 * E3b, 23.10.2026).
 */
function woherStation(stationen: readonly Station[], stationId: string): string {
  const nr = stationen.find((s) => s.id === stationId)?.nr;
  const name = STATIONEN.find((s) => s.nr === nr)?.name;
  return name ? `aus Station ${nr}: ${name}` : "aus einer Station";
}

/**
 * Eine Zeile „eingetragen“: Bezeichnung, woher, die Menge zum Ändern (oder
 * zum Lesen), ✕. Ein Tipp auf die Menge ändert die Menge; bei eigenen
 * Angaben („Etwas anderes“) öffnet ein Tipp auf den Namen an derselben
 * Stelle das Formular zum Ändern (Gernot, Augentest E3b, 23.10.2026).
 */
function MaterialZeile({ m, modus, woher }: { m: Materialangabe; modus: Bearbeiten; woher: string | null }) {
  const [text, setText] = useState(() => mengeZeigen(m.menge));
  const [problem, setProblem] = useState<string | null>(null);
  const [aendern, setAendern] = useState(false);
  const nurLesen = modus.art === "gesperrt";
  // Im Nachtrag an Material, das schon im Büro ist: berichtigen ja, entfernen nein.
  const wegGeht = entfernbar(m, modus);
  const textGeht = textAenderbar(m, modus);
  const marke = materialMarke(m, modus);
  const speichern = useSpeichern(async (menge: number) => {
    await materialMenge(m.id, menge);
  });
  const id = `menge-${m.id}`;

  if (aendern) {
    return (
      <DazuFormular
        kachel="anderes"
        vorher={{ m, mengeText: typeof mengeLesen(text) === "number" ? text : mengeZeigen(m.menge) }}
        aufnahmeId={m.aufnahmeId}
        stationId={m.stationId}
        fertig={(menge) => {
          // Blieb die Zeile (nur die Menge war anders), zeigt ihr Feld die neue Menge.
          if (menge !== undefined) setText(mengeZeigen(menge));
          setProblem(null);
          setAendern(false);
        }}
      />
    );
  }

  const worte = (
    <>
      <b>
        {m.bezeichnung}
        {textGeht && <Ikon name="stift" groesse="k" />}
        {marke && <span className="sendungs-marke">{marke}</span>}
      </b>
      {(woher || m.artikelnummer) && <span>{[woher, m.artikelnummer].filter(Boolean).join(" · ")}</span>}
      {problem && <span className="feld-hinweis">{problem}</span>}
    </>
  );

  async function entfernen() {
    speichern.verwerfen();
    try {
      await materialEntfernen(m.id);
      toastZeigen(`${m.bezeichnung} entfernt`);
    } catch (fehler) {
      speichernFehlgeschlagen(fehler, "Konnte nicht entfernen – bitte noch einmal.");
    }
  }

  return (
    <div className={`material-zeile${problem ? " ungueltig" : ""}`}>
      {textGeht ? (
        <button
          type="button"
          className="material-wort aenderbar"
          title="Antippen zum Ändern"
          onClick={() => {
            // Eine eben getippte Menge zuerst sichern – das Formular beginnt mit ihr.
            speichern.sofort();
            setAendern(true);
          }}
        >
          {worte}
        </button>
      ) : (
        <label className="material-wort" htmlFor={nurLesen ? undefined : id}>
          {worte}
        </label>
      )}
      {nurLesen ? (
        <span className="material-menge nur-lesen">
          <span className="material-zahl">{mengeZeigen(m.menge)}</span>
          <span className="einheit">{m.einheit}</span>
        </span>
      ) : (
        <span className="material-menge">
          <input
            id={id}
            inputMode="decimal"
            enterKeyHint="done"
            autoComplete="off"
            maxLength={9}
            value={text}
            aria-invalid={problem ? true : undefined}
            onChange={(e) => {
              setText(e.target.value);
              const menge = mengeLesen(e.target.value);
              if (menge === null) {
                // Leer speichert nichts – es gilt die letzte Menge.
                speichern.verwerfen();
                setProblem(wegGeht ? "Bitte eine Menge – oder mit ✕ entfernen." : "Leer geht nicht – trag 0 ein, wenn es doch nicht gebraucht wird.");
                return;
              }
              if (menge === undefined) {
                setProblem("Bitte eine Zahl, z. B. 25 oder 12,5.");
                return;
              }
              setProblem(null);
              speichern.spaeter(menge);
            }}
            onBlur={speichern.sofort}
          />
          <span className="einheit">{m.einheit}</span>
        </span>
      )}
      {wegGeht && (
        <button type="button" className="material-weg" aria-label={`${m.bezeichnung} entfernen`} title="Entfernen" onClick={() => void entfernen()}>
          <Ikon name="kreuz" groesse="k" />
        </button>
      )}
    </div>
  );
}

/** Die Kacheln zum Antippen – und am Ende immer „Etwas anderes“. */
function KachelRaster({ liste, waehlen }: { liste: readonly Kachel[]; waehlen: (k: Kachel | "anderes") => void }) {
  return (
    <div className="material-kacheln">
      {liste.map((k) => (
        <button key={k.schluessel} type="button" className="material-kachel" onClick={() => waehlen(k)}>
          <b>{k.titel}</b>
          <span>{k.varianten.length > 1 ? `${k.varianten.length} Varianten` : k.einheit ? `in ${k.einheit}` : ""}</span>
        </button>
      ))}
      <button type="button" className="material-kachel anderes" onClick={() => waehlen("anderes")}>
        <b>
          <Ikon name="plus" groesse="k" /> Etwas anderes
        </b>
        <span>frei eintippen</span>
      </button>
    </div>
  );
}

/**
 * Das Formular „Dazu“ an der Stelle der Kacheln: bei einer Gruppe zuerst die
 * Variante, dann die Menge (vorbelegt mit der Vorgabe der Liste, wenn es
 * eine gibt); bei „Etwas anderes“ Bezeichnung, Menge und Einheit.
 *
 * Mit `vorher` dasselbe Formular zum Ändern einer eigenen Angabe, an der
 * Stelle ihrer Zeile – vorbefüllt, Knopf „Ändern“ (`materialAendern`).
 * `fertig` bekommt dann die Menge mit, die danach gilt.
 */
function DazuFormular({
  kachel,
  vorher,
  aufnahmeId,
  stationId,
  fertig,
}: {
  kachel: Kachel | "anderes";
  vorher?: { m: Materialangabe; mengeText: string };
  aufnahmeId: string;
  stationId: string | null;
  fertig: (menge?: number) => void;
}) {
  const anderes = kachel === "anderes";
  const varianten = anderes ? [] : kachel.varianten;
  const [eintrag, setEintrag] = useState<StandardEintrag | null>(() => (varianten.length === 1 ? varianten[0]! : null));
  const [menge, setMenge] = useState(() => vorher?.mengeText ?? (varianten.length === 1 ? vorgabeText(varianten[0]!) : ""));
  const [bezeichnung, setBezeichnung] = useState(() => vorher?.m.bezeichnung ?? "");
  const [einheit, setEinheit] = useState(() => vorher?.m.einheit ?? "Stk");
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  // Zweimal „Fertig“ auf der Handy-Tastatur trägt nicht doppelt ein (Code-Prüfung E3b, Befund 3) – eine Ref,
  // weil der Zustand `sendet` erst nach dem nächsten Zeichnen gilt.
  const laeuft = useRef(false);
  const karte = useRef<HTMLDivElement>(null);
  const titel = vorher ? "Ändern" : anderes ? "Etwas anderes" : kachel.titel;
  // Eigene Kennungen für das Formular zum Ändern – „Dazu“ kann gleichzeitig offen sein.
  const feld = vorher ? `aendern-${vorher.m.id}` : "dazu";

  // Die Karte steht dort, wo eben die Kacheln waren – ganz ins Bild holen.
  useEffect(() => {
    karte.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  function variante(e: StandardEintrag) {
    // Die Vorgabe der neuen Variante, solange noch nichts Eigenes getippt ist.
    if (menge.trim() === "" || (eintrag && menge === vorgabeText(eintrag))) setMenge(vorgabeText(e));
    setEintrag(e);
    setMeldung(null);
  }

  async function dazu() {
    if (laeuft.current) return;
    if (!anderes && !eintrag) {
      setMeldung("Bitte zuerst wählen, welche.");
      return;
    }
    const geprueft = formularPruefen({ eintrag: anderes ? null : eintrag, bezeichnung, einheit, menge }, stationId);
    if (!geprueft.ok) {
      setMeldung(geprueft.meldung);
      return;
    }
    laeuft.current = true;
    setSendet(true);
    try {
      if (vorher) {
        await materialAendern(vorher.m.id, geprueft.neu);
        toastZeigen(`${materialText(geprueft.neu)} – geändert`);
        fertig(geprueft.neu.menge);
        return;
      }
      await materialDazu(aufnahmeId, geprueft.neu);
      toastZeigen(`${materialText(geprueft.neu)} – eingetragen`);
      fertig();
    } catch (fehler) {
      speichernFehlgeschlagen(fehler);
      laeuft.current = false;
      setSendet(false);
    }
  }

  const gruppe = anderes ? "" : kachel.titel;
  const einheitFest = anderes ? null : (eintrag?.einheit ?? kachel.einheit);
  return (
    <div className="material-dazu" ref={karte}>
      <div className="material-dazu-kopf">
        <b>{titel}</b>
        <button type="button" className="material-weg" aria-label={vorher ? "Schließen, nichts ändern" : "Schließen, nichts eintragen"} title="Schließen" onClick={() => fertig()}>
          <Ikon name="kreuz" groesse="k" />
        </button>
      </div>
      {varianten.length > 1 && (
        <div className="auswahl material-varianten" role="radiogroup" aria-label={`Welche ${titel}?`}>
          {varianten.map((v) => (
            <button key={v.id} type="button" role="radio" aria-checked={eintrag?.id === v.id} className={eintrag?.id === v.id ? "an" : undefined} onClick={() => variante(v)}>
              {variantenName(v, gruppe)}
            </button>
          ))}
        </div>
      )}
      {anderes && (
        <div className="feld">
          <label htmlFor={`${feld}-bezeichnung`}>Was ist es?</label>
          <input
            id={`${feld}-bezeichnung`}
            value={bezeichnung}
            maxLength={200}
            autoComplete="off"
            enterKeyHint="next"
            placeholder="z. B. Erdungsband"
            onChange={(e) => {
              setBezeichnung(e.target.value);
              setMeldung(null);
            }}
          />
        </div>
      )}
      <div className="material-dazu-zeile">
        <label className="laenge-feld" htmlFor={`${feld}-menge`}>
          <span className="laenge-name">Menge</span>
          <span className="laenge-wert">
            <input
              id={`${feld}-menge`}
              inputMode="decimal"
              enterKeyHint="done"
              autoComplete="off"
              placeholder="–"
              maxLength={9}
              value={menge}
              onChange={(e) => {
                setMenge(e.target.value);
                setMeldung(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void dazu();
              }}
            />
            {einheitFest !== null && <span className="einheit">{einheitFest}</span>}
          </span>
        </label>
        {anderes && (
          <label className="laenge-feld einheit-feld" htmlFor={`${feld}-einheit`}>
            <span className="laenge-name">Einheit</span>
            <span className="laenge-wert">
              <input id={`${feld}-einheit`} autoComplete="off" maxLength={20} value={einheit} onChange={(e) => setEinheit(e.target.value)} />
            </span>
          </label>
        )}
      </div>
      {meldung && <div className="feld-hinweis">{meldung}</div>}
      <button type="button" className="knopf-haupt knopf-block" disabled={sendet} onClick={() => void dazu()}>
        {vorher ? (
          <>
            <Ikon name="haken" /> Ändern
          </>
        ) : (
          <>
            <Ikon name="plus" /> Eintragen
          </>
        )}
      </button>
    </div>
  );
}

function MaterialInhalt({ ich, ganz, station }: { ich: IchAntwort; ganz: AufnahmeGanz; station: Station | null }) {
  const { aufnahme, stationen } = ganz;
  const modus = modusVon(ganz);
  const nurLesen = modus.art === "gesperrt";
  const post = usePostausgang();
  const standard = useLive(standardlisteLesen, "standardliste") ?? [];
  const [gewaehlt, setGewaehlt] = useState<Kachel | "anderes" | null>(null);
  // Die Liste vom Büro auffrischen – beim Öffnen (höchstens einmal je Minute, ohne Netz gar nicht).
  const firmaId = ich.firma.id;
  useEffect(() => {
    if (!nurLesen) standardlisteAnstossen(firmaId);
  }, [nurLesen, firmaId]);

  const st = station ? STATIONEN.find((s) => s.nr === station.nr) : undefined;
  const hier = materialOrdnen(
    station ? ganz.materialangaben.filter((m) => m.stationId === station.id) : ganz.materialangaben,
    stationen,
  );
  const zurUebersicht = `#/aufnahme/${aufnahme.id}`;
  const zurStation = station ? `${zurUebersicht}/station/${station.nr}` : null;
  const letzte = STATIONEN.find((s) => s.nr === STATIONEN_ZAHL);
  const k = kacheln(standard);

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        // Oben links kurz „Station 2“ – ein langer Name („Weg vom Dach zum Technikraum“) drängte den Sendestand hinaus.
        zurueck={zurStation && station ? { adresse: zurStation, name: `Station ${station.nr}` } : { adresse: zurUebersicht, name: "Übersicht" }}
        titel={station ? "Material" : "Am Schluss"}
        rechts={<SendeStand wartend={post.wartend} />}
        gross={
          <>
            Material
            {modus.art === "nachtrag" && (
              <Chip art="lime" ikon="plus">
                Nachtrag
              </Chip>
            )}
          </>
        }
        unter={st ? `aus Station ${st.nr}: ${st.name}` : "Was du schon weißt – freiwillig"}
      />
      <main className="app-inhalt">
        {modus.art === "aufnahme" && (
          <Tipp art="grau">
            {station
              ? "Was du hier an Material siehst: antippen, Menge dazu. Im Büro steht dabei, aus welcher Station es kommt."
              : "Antippen, was gebraucht wird, und die Menge dazu. Das Büro bekommt es bei der Freigabe vorbelegt und entscheidet dort."}
          </Tipp>
        )}
        {modus.art === "nachtrag" && (
          <Tipp art="gelb" ikon="plus">
            <b>Nachtrag:</b> Neues kommt dazu. Bei Material, das schon im Büro ist, lässt sich die Menge berichtigen – 0, wenn es doch
            nicht gebraucht wird.
          </Tipp>
        )}

        {(hier.length > 0 || nurLesen) && (
          <>
            <div className="abschnitt-titel">{station ? "Aus dieser Station" : "Eingetragen"}</div>
            {hier.length > 0 ? (
              <div className="material-liste">
                {hier.map((m) => (
                  <MaterialZeile
                    key={m.id}
                    m={m}
                    modus={modus}
                    woher={station ? null : m.stationId ? woherStation(stationen, m.stationId) : null}
                  />
                ))}
              </div>
            ) : (
              <div className="text-karte nur-lesen">
                <p className="lese-text leer">{station ? "Kein Material aus dieser Station." : "Kein Material angegeben."}</p>
              </div>
            )}
          </>
        )}

        {!nurLesen && (
          <>
            <div className="abschnitt-titel">{gewaehlt ? "Dazu" : hier.length > 0 ? "Noch etwas dazu?" : "Zum Antippen"}</div>
            {gewaehlt ? (
              <DazuFormular
                key={gewaehlt === "anderes" ? "anderes" : gewaehlt.schluessel}
                kachel={gewaehlt}
                aufnahmeId={aufnahme.id}
                stationId={station?.id ?? null}
                fertig={() => setGewaehlt(null)}
              />
            ) : (
              <>
                <KachelRaster liste={k} waehlen={setGewaehlt} />
                {k.length === 0 && (
                  <p className="klein-text" style={{ margin: "0 2px" }}>
                    Die Liste zum Antippen kommt vom Büro, sobald Netz da ist. „Etwas anderes“ geht immer.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </main>
      <AppFuss>
        {zurStation && station ? (
          <a href={zurStation} className="knopf-haupt knopf-block">
            {nurLesen ? <Ikon name="links" /> : <Ikon name="haken" />} {nurLesen ? `Zurück zur Station ${station.nr}` : `Fertig – zurück zur Station ${station.nr}`}
          </a>
        ) : (
          <div className="knopf-reihe">
            <a
              href={`${zurUebersicht}/station/${STATIONEN_ZAHL}`}
              className="knopf-rand knopf-vorige"
              aria-label={`Zurück zur Station ${STATIONEN_ZAHL}: ${letzte?.name ?? ""}`}
              title={`Zurück zur Station ${STATIONEN_ZAHL}: ${letzte?.name ?? ""}`}
            >
              <Ikon name="links" />
              <span>{letzte?.name}</span>
            </a>
            {modus.art === "aufnahme" ? (
              <a href={`${zurUebersicht}/k2`} className="knopf-haupt knopf-naechste">
                <span>Weiter: Dachplanung mit K2</span>
                <Ikon name="rechts" />
              </a>
            ) : (
              <a href={zurUebersicht} className="knopf-haupt knopf-naechste">
                <span>Zur Übersicht</span>
                <Ikon name="rechts" />
              </a>
            )}
          </div>
        )}
      </AppFuss>
    </div>
  );
}

export function MaterialSeite({ ich, aufnahmeId, nr }: { ich: IchAntwort; aufnahmeId: string; nr?: number }) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  if (ganz === undefined) return <div className="app-seite" />;
  const station = nr === undefined ? null : (ganz?.stationen.find((s) => s.nr === nr) ?? null);
  if (!ganz || (nr !== undefined && !station)) {
    return (
      <div className="app-seite">
        <AppKopf umgebung={ich.umgebung} zurueck={ZUM_START} titel="Material" gross="Nicht gefunden" />
        <main className="app-inhalt">
          <Tipp art="grau">Diese Besichtigung ist auf diesem Gerät nicht gespeichert.</Tipp>
        </main>
      </div>
    );
  }
  // Je Modus ein eigenes Formular – beginnt oder endet ein Nachtrag, gehören die Felder zu einer anderen Sendung.
  const modus = modusVon(ganz);
  return <MaterialInhalt key={`${station?.id ?? "schluss"}-${modus.art === "nachtrag" ? modus.nachtragId : modus.art}`} ich={ich} ganz={ganz} station={station} />;
}
